import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FaHardHat, FaBuilding, FaClipboardList, FaHome,
  FaArrowLeft, FaUserTie, FaUser, FaCheck,
  FaEye, FaEyeSlash, FaExclamationCircle, FaEnvelopeOpenText
} from 'react-icons/fa';
import './Register.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { getApiEndpoint } from '../../../config/api';
import { checkPassword, PASSWORD_MIN } from '../../../utils/passwordStrength';
import PasswordMeter from '../PasswordMeter/PasswordMeter';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// The server only enforces the bot check in production, so a missing key only blocks sign-up there.
const CAPTCHA_BLOCKS_SIGNUP = !RECAPTCHA_SITE_KEY && process.env.NODE_ENV === 'production';

const ROLE_CATEGORIES = [
  {
    id: 'contractor',
    icon: FaHardHat,
    title: 'Contractor',
    description: 'I manage or work for a contracting company',
    subRoles: [
      { id: 'contractor_admin', label: 'Company Admin', desc: 'I own or manage the contractor company', icon: FaUserTie },
      { id: 'contractor_staff', label: 'Engineer / Staff', desc: 'I carry out work and testing on site', icon: FaUser },
    ]
  },
  {
    id: 'building_manager',
    icon: FaBuilding,
    title: 'Building Manager',
    description: 'I manage building access and on-site operations',
    subRoles: null
  },
  {
    id: 'fm',
    icon: FaClipboardList,
    title: 'FM / Admin',
    description: 'I own or manage buildings and oversee contractors and compliance',
    subRoles: null
  },
  {
    id: 'owner',
    icon: FaHome,
    title: 'Property Owner',
    description: 'I own or oversee properties and want to view compliance reports',
    subRoles: null
  }
];

// UK numbers (07…, 01…, 02…, 03…, +44…) or any international number with a + prefix.
const isValidPhone = (v) => {
  const d = String(v || '').replace(/[\s()-]/g, '');
  if (/^(?:\+44|0044)\d{9,10}$/.test(d)) return true;
  if (/^0\d{9,10}$/.test(d)) return true;
  return /^\+[1-9]\d{7,14}$/.test(d);
};

const ROLE_MAP = {
  contractor_admin: 'contractor',
  contractor_staff: 'contractor',
  building_manager: 'building_manager',
  fm: 'fm',
  owner: 'viewer'
};

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState('select');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubRole, setSelectedSubRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef(null);
  // After sign-up, when the server asks for email verification.
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | failed

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    companyName: '',
  });

  const effectiveRole = selectedSubRole || selectedCategory?.id;
  const dbRole = effectiveRole ? ROLE_MAP[effectiveRole] || effectiveRole : 'viewer';
  const isCompanyAdmin = selectedSubRole === 'contractor_admin';
  const needsCompanyName = ['contractor_admin', 'fm'].includes(effectiveRole);

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setSelectedSubRole(null);
    setStep(cat.subRoles ? 'sub-select' : 'form');
  };

  const handleSubRoleSelect = (subId) => {
    setSelectedSubRole(subId);
    setStep('form');
  };

  const goBack = () => {
    setServerError('');
    if (step === 'form' && selectedCategory?.subRoles) {
      setStep('sub-select');
      setSelectedSubRole(null);
    } else if (step === 'form' || step === 'sub-select') {
      setStep('select');
      setSelectedCategory(null);
      setSelectedSubRole(null);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'firstName':
        if (!value.trim()) error = 'First name is required';
        break;
      case 'lastName':
        if (!value.trim()) error = 'Last name is required';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Please enter a valid email address';
        break;
      case 'password':
        error = checkPassword(value, [formData.email, formData.firstName, formData.lastName]).error;
        break;
      case 'confirmPassword':
        if (!value) error = 'Please confirm your password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;
      case 'phoneNumber':
        if (!value.trim()) error = 'Phone number is required';
        else if (!isValidPhone(value)) error = 'Enter a valid phone number, e.g. 07700 900123 or +44 7700 900123';
        break;
      case 'companyName':
        if (needsCompanyName && !value.trim()) error = 'Company name is required';
        break;
      default: break;
    }
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) setServerError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
    if (name === 'password') setPasswordFocused(false);
  };

  const handlePasswordFocus = () => {
    setPasswordFocused(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    // Validate all fields
    const fields = ['firstName', 'lastName', 'email', 'phoneNumber', 'password', 'confirmPassword'];
    if (needsCompanyName) fields.push('companyName');

    const allTouched = {};
    let hasError = false;
    fields.forEach(name => {
      allTouched[name] = true;
      const err = validateField(name, formData[name]);
      if (err) hasError = true;
    });
    setTouched(prev => ({ ...prev, ...allTouched }));

    if (hasError) return;

    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      setCaptchaError('Tick "I\'m not a robot" to continue');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phoneNumber: formData.phoneNumber?.trim() || null,
          role: effectiveRole === 'fm' ? 'fm' : dbRole,
          companyName: needsCompanyName ? (formData.companyName || null) : null,
          isCompanyAdmin: isCompanyAdmin,
          accountType: effectiveRole,
          captchaToken,
        })
      });

      let data;
      try {
        data = await res.json();
      } catch (_) {
        throw new Error('Registration failed. Please try again.');
      }
      if (!res.ok) {
        // A CAPTCHA token can only be used once, so any failure needs a fresh tick.
        resetCaptcha();
        if (data.captchaFailed) setCaptchaError('Verification expired or failed — tick the box again');
        throw new Error(data.message || `Registration failed (${res.status})`);
      }

      if (data.emailVerificationRequired) {
        setVerifyEmail(formData.email.trim().toLowerCase());
        setResendState('idle');
        setStep('verify');
        return;
      }
      toast.success('Account created — sign in to get started');
      navigate('/login');
    } catch (error) {
      resetCaptcha();
      setServerError(error.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    try { captchaRef.current?.reset(); } catch { /* widget not mounted */ }
  };

  const resendVerification = async () => {
    setResendState('sending');
    try {
      const res = await fetch(getApiEndpoint('/api/auth/resend-verification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail }),
      });
      setResendState(res.ok ? 'sent' : 'failed');
    } catch {
      setResendState('failed');
    }
  };

  const getCategoryLabel = () => {
    if (!selectedCategory) return '';
    if (selectedSubRole) {
      const sub = selectedCategory.subRoles?.find(s => s.id === selectedSubRole);
      return `${selectedCategory.title} — ${sub?.label}`;
    }
    return selectedCategory.title;
  };

  const showPasswordHint = passwordFocused || (touched.password && fieldErrors.password);

  return (
    <div className="reg-page">
      <header className="reg-header">
        <img src={allSafeLogo} alt="CloudSafe" className="reg-logo" />
        <div className="reg-header-right">
          <span className="reg-have-account">Already have an account?</span>
          <Link to="/login" className="reg-sign-in-link">Sign in</Link>
        </div>
      </header>

      <main className="reg-content">
        <div className="reg-card">

          {/* ─── Check your email ───────────── */}
          {step === 'verify' && (
            <div className="reg-step" style={{ textAlign: 'center' }}>
              <FaEnvelopeOpenText aria-hidden="true" style={{ fontSize: 40, color: 'var(--cs-blue, #1A80E5)', margin: '0 auto 12px', display: 'block' }} />
              <h1 className="reg-title">Check your email to verify your account</h1>
              <p className="reg-subtitle">
                We've sent a link to <strong>{verifyEmail}</strong>. Click it to verify your address, then sign in.
                The link lasts 48 hours.
              </p>
              <p className="reg-subtitle">Can't see it? Check your spam or junk folder.</p>
              <div role="status" aria-live="polite" style={{ minHeight: 24, marginBottom: 12, fontSize: 'var(--cs-text-sm)' }}>
                {resendState === 'sent' && <span style={{ color: 'var(--cs-green-dark, #15803d)' }}>If your account still needs verifying, a new link is on its way.</span>}
                {resendState === 'failed' && <span style={{ color: 'var(--cs-red-dark, #b91c1c)' }}>We couldn't send a new link — check your connection and try again.</span>}
              </div>
              <button type="button" className="reg-submit" onClick={resendVerification} disabled={resendState === 'sending'}>
                {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
              </button>
              <p className="reg-terms"><Link to="/login">Go to sign in</Link></p>
            </div>
          )}

          {/* ─── Step 1: Role Selection ──────── */}
          {step === 'select' && (
            <div className="reg-step">
              <h1 className="reg-title">Create an account</h1>
              <p className="reg-subtitle">Choose your account type to get started.</p>

              <div className="reg-role-grid">
                {ROLE_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      className="reg-role-card"
                      onClick={() => handleCategorySelect(cat)}
                      type="button"
                    >
                      <div className="reg-role-icon-wrap">
                        <Icon className="reg-role-icon" />
                      </div>
                      <h3 className="reg-role-title">{cat.title}</h3>
                      <p className="reg-role-desc">{cat.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Step 1b: Sub-role Selection ── */}
          {step === 'sub-select' && selectedCategory?.subRoles && (
            <div className="reg-step">
              <button className="reg-back" onClick={goBack} type="button">
                <FaArrowLeft /> Back
              </button>
              <h1 className="reg-title">{selectedCategory.title}</h1>
              <p className="reg-subtitle">Which best describes your role?</p>

              <div className="reg-sub-grid">
                {selectedCategory.subRoles.map(sub => {
                  const Icon = sub.icon;
                  return (
                    <button
                      key={sub.id}
                      className="reg-sub-card"
                      onClick={() => handleSubRoleSelect(sub.id)}
                      type="button"
                    >
                      <Icon className="reg-sub-icon" />
                      <div>
                        <h4 className="reg-sub-title">{sub.label}</h4>
                        <p className="reg-sub-desc">{sub.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Step 2: Registration Form ──── */}
          {step === 'form' && (
            <div className="reg-step">
              <div className="reg-top-row">
                <button className="reg-back" onClick={goBack} type="button">
                  <FaArrowLeft /> Back
                </button>
                <div className="reg-chosen-role">
                  <FaCheck className="reg-chosen-check" />
                  <span>{getCategoryLabel()}</span>
                </div>
              </div>

              <h1 className="reg-title">Your details</h1>
              <p className="reg-subtitle">Fill in your information to get started.</p>

              {serverError && (
                <div className="reg-error-alert" role="alert">
                  <FaExclamationCircle className="reg-error-alert-icon" />
                  <span>{serverError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="reg-form" noValidate>
                {/* Name row */}
                <div className="reg-row">
                  <div className="reg-field">
                    <label className="reg-label" htmlFor="reg-firstName">First name</label>
                    <input
                      id="reg-firstName"
                      name="firstName"
                      className={`reg-input${touched.firstName && fieldErrors.firstName ? ' has-error' : ''}`}
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="First name"
                      autoComplete="given-name"
                      aria-describedby={touched.firstName && fieldErrors.firstName ? 'reg-firstName-error' : undefined}
                      required
                    />
                    {touched.firstName && fieldErrors.firstName && (
                      <p className="reg-field-error" id="reg-firstName-error" role="alert">{fieldErrors.firstName}</p>
                    )}
                  </div>
                  <div className="reg-field">
                    <label className="reg-label" htmlFor="reg-lastName">Last name</label>
                    <input
                      id="reg-lastName"
                      name="lastName"
                      className={`reg-input${touched.lastName && fieldErrors.lastName ? ' has-error' : ''}`}
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Last name"
                      autoComplete="family-name"
                      aria-describedby={touched.lastName && fieldErrors.lastName ? 'reg-lastName-error' : undefined}
                      required
                    />
                    {touched.lastName && fieldErrors.lastName && (
                      <p className="reg-field-error" id="reg-lastName-error" role="alert">{fieldErrors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="reg-field">
                  <label className="reg-label" htmlFor="reg-email">Email address</label>
                  <input
                    id="reg-email"
                    name="email"
                    type="email"
                    className={`reg-input${touched.email && fieldErrors.email ? ' has-error' : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="you@company.com"
                    autoComplete="email"
                    inputMode="email"
                    aria-describedby={touched.email && fieldErrors.email ? 'reg-email-error' : undefined}
                    required
                  />
                  {touched.email && fieldErrors.email && (
                    <p className="reg-field-error" id="reg-email-error" role="alert">{fieldErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="reg-field">
                  <label className="reg-label" htmlFor="reg-phone">Phone number</label>
                  <input
                    id="reg-phone"
                    name="phoneNumber"
                    type="tel"
                    className={`reg-input${touched.phoneNumber && fieldErrors.phoneNumber ? ' has-error' : ''}`}
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="+44 7xxx xxxxxx"
                    autoComplete="tel"
                    inputMode="tel"
                    aria-describedby={touched.phoneNumber && fieldErrors.phoneNumber ? 'reg-phone-error' : undefined}
                    required
                  />
                  {touched.phoneNumber && fieldErrors.phoneNumber && (
                    <p className="reg-field-error" id="reg-phone-error" role="alert">{fieldErrors.phoneNumber}</p>
                  )}
                </div>

                {/* Company name (conditional) */}
                {needsCompanyName && (
                  <div className="reg-field">
                    <label className="reg-label" htmlFor="reg-company">Company name</label>
                    <input
                      id="reg-company"
                      name="companyName"
                      className={`reg-input${touched.companyName && fieldErrors.companyName ? ' has-error' : ''}`}
                      value={formData.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your company name"
                      autoComplete="organization"
                      aria-describedby={touched.companyName && fieldErrors.companyName ? 'reg-company-error' : undefined}
                      required
                    />
                    {touched.companyName && fieldErrors.companyName && (
                      <p className="reg-field-error" id="reg-company-error" role="alert">{fieldErrors.companyName}</p>
                    )}
                  </div>
                )}

                {/* Password */}
                <div className="reg-field">
                  <label className="reg-label" htmlFor="reg-password">Password</label>
                  <div className="reg-input-wrap">
                    <input
                      id="reg-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`reg-input${touched.password && fieldErrors.password ? ' has-error' : ''}`}
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onFocus={handlePasswordFocus}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      aria-describedby={`reg-password-hint${touched.password && fieldErrors.password ? ' reg-password-error' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      className="reg-pw-toggle"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {(showPasswordHint || formData.password) && !fieldErrors.password && (
                    <p className="reg-password-hint" id="reg-password-hint">At least {PASSWORD_MIN} characters. A short phrase of three or four words is easy to remember and hard to guess.</p>
                  )}
                  <PasswordMeter value={formData.password} context={[formData.email, formData.firstName, formData.lastName]} />
                  {touched.password && fieldErrors.password && (
                    <p className="reg-field-error" id="reg-password-error" role="alert">{fieldErrors.password}</p>
                  )}
                </div>

                {/* Confirm password */}
                <div className="reg-field">
                  <label className="reg-label" htmlFor="reg-confirmPassword">Confirm password</label>
                  <div className="reg-input-wrap">
                    <input
                      id="reg-confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`reg-input${touched.confirmPassword && fieldErrors.confirmPassword ? ' has-error' : ''}`}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      aria-describedby={touched.confirmPassword && fieldErrors.confirmPassword ? 'reg-confirmPassword-error' : undefined}
                      required
                    />
                    <button
                      type="button"
                      className="reg-pw-toggle"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {touched.confirmPassword && fieldErrors.confirmPassword && (
                    <p className="reg-field-error" id="reg-confirmPassword-error" role="alert">{fieldErrors.confirmPassword}</p>
                  )}
                </div>

                {/* Bot check — the server refuses sign-ups without it */}
                <div className="reg-field">
                  {RECAPTCHA_SITE_KEY ? (
                    <ReCAPTCHA
                      ref={captchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(v) => { setCaptchaToken(v); setCaptchaError(''); }}
                      onExpired={() => { setCaptchaToken(null); setCaptchaError('Verification expired — tick the box again'); }}
                      onErrored={() => setCaptchaError('The verification check could not load. Allow scripts from google.com, or turn off content blockers, then reload the page.')}
                    />
                  ) : CAPTCHA_BLOCKS_SIGNUP ? (
                    <p className="reg-field-error" role="alert">
                      Sign-up verification isn't configured on this site, so new accounts can't be created right now. Contact support@cloud-safe.uk.
                    </p>
                  ) : (
                    <p className="reg-password-hint">
                      Development build: no reCAPTCHA site key is set, so the bot check is skipped.
                    </p>
                  )}
                  {captchaError && <p className="reg-field-error" role="alert">{captchaError}</p>}
                </div>

                {/* Submit */}
                <button type="submit" className="reg-submit" disabled={submitting || CAPTCHA_BLOCKS_SIGNUP}>
                  {submitting && <span className="reg-spinner" />}
                  {submitting ? 'Creating account...' : 'Create account'}
                </button>
              </form>

              <p className="reg-terms">
                By creating an account, you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
